import { Controller } from "@hotwired/stimulus"
import PusherManager from "../services/pusher_manager"
import { turboFetch } from "../util/common"

// Connects to data-controller="dialog-messages"
export default class extends Controller {
  static targets = [
    "dialogContainer",
    "loadTrigger",
    "spinner",
    "replyButton",
    "replyContainer",
    "replyName",
    "replyText",
    "parentHiddenInput"
  ]

  static values = {
    universityId: Number,
    userId: Number,
    loadReportedMessage: {
      type: Boolean,
      default: false
    },
    messagesUrlBase: String,
    nextMessageUrlBase: String,
    reportedMessageUrl: String,
    pinnedMessagesUrl: String,
    targetMessageId: Number,
    chatsListUrl: String,
    messagesFrameId: String
  }

  hasMoreMessages = false
  isLoadingMore = false
  isDisabledResetScroll = false

  connect() {
    this.handleNewDialogMessage = this.handleNewDialogMessage.bind(this)
    this.handleDeletedDialogMessage = this.handleDeletedDialogMessage.bind(this)
    this.handleChatReaction = this.handleChatReaction.bind(this)
    this.handleChatPinMessage = this.handleChatPinMessage.bind(this)
    this.handleChatUnpinMessage = this.handleChatUnpinMessage.bind(this)
    this.handleChatDeleteMessage = this.handleChatDeleteMessage.bind(this)
    this.handleChatUserBlocked = this.handleChatUserBlocked.bind(this)
    this.handleChatUserUnblocked = this.handleChatUserUnblocked.bind(this)

    if (!PusherManager.isInitialized()) {
      PusherManager.initialize()
    }

    this.initScroll()
    this.setupPusherSubscriptions()

    if (this.isChat()) {
      this.setupChatSubscriptions()
    }

    this.initReportedMessageScroll()
    this.initTargetMessageScroll()

    document.addEventListener("tab:changed", () => {
      if (this.isActivePanel()) {
        this.initScrollForTabSwitch()
      }
    })
  }

  disconnect() {
    if (this.autoScrollObserver) {
      this.autoScrollObserver.disconnect()
      this.autoScrollObserver = null
    }
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect()
      this.intersectionObserver = null
    }
    if (this.loadScrollObserver) {
      this.loadScrollObserver.disconnect()
      this.loadScrollObserver = null
    }

    PusherManager.unsubscribe(this.handleNewDialogMessage)
    PusherManager.unsubscribe(this.handleDeletedDialogMessage)

    if (this.isChat()) {
      PusherManager.unsubscribe(this.handleChatReaction)
      PusherManager.unsubscribe(this.handleChatPinMessage)
      PusherManager.unsubscribe(this.handleChatUnpinMessage)
      PusherManager.unsubscribe(this.handleChatDeleteMessage)
      PusherManager.unsubscribe(this.handleChatUserBlocked)
      PusherManager.unsubscribe(this.handleChatUserUnblocked)
    }

    this.reportedMessageId = null
  }

  initScroll() {
    const container = this.dialogContainerTarget

    this.dialogId = container.dataset.dialogId
    this.oldestMessageId = container.dataset.oldestMessageId
    const firstMessageId = container.querySelector("[data-message-id]")?.dataset.messageId
    this.hasMoreMessages = this.oldestMessageId !== firstMessageId

    this.resetScrollInit(container)
    this.setAutoScroll(container)
    this.setupInfiniteScroll()

    this.element.addEventListener("send-message:send", this.clearReply.bind(this))
  }

  initScrollForTabSwitch() {
    const container = this.dialogContainerTarget

    this.dialogId = container.dataset.dialogId
    this.oldestMessageId = container.dataset.oldestMessageId
    this.hasMoreMessages = this.oldestMessageId !== container.querySelector("[data-message-id]")?.dataset.messageId

    this.resetScroll(container)
    this.setAutoScroll(container)
    this.setupInfiniteScroll()
  }

  setAutoScroll(container) {
    if (this.autoScrollObserver)
      this.autoScrollObserver.disconnect()

    if (!this.autoScrollObserver) {
      this.autoScrollObserver = new MutationObserver(mutations => {
        if (this.isLoadingMore || this.isDisabledResetScroll)
          return

        mutations.forEach(mutation => {
          if (mutation.addedNodes.length) {
            setTimeout(() => this.resetScroll(container), 150)
          }
        })
      })
    }

    this.autoScrollObserver.observe(container, { childList: true, subtree: true })
  }

  resetScrollInit(container) {
    container.classList.add("invisible")
    container.style.scrollBehavior = "auto"
    this.resetScroll(container)

    setTimeout(() => {
      container.classList.remove("invisible")
      container.style.scrollBehavior = "smooth"
    }, 700)
  }

  resetScroll(container) {
    container.scrollTop = container.scrollHeight - container.clientHeight
  }

  setupInfiniteScroll() {
    if (!this.hasLoadTriggerTarget) return

    const loadMoreTrigger = this.loadTriggerTarget

    this.intersectionObserver = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && this.hasMoreMessages && !this.isLoadingMore) {
        this.loadMore()
      }
    })

    this.intersectionObserver.observe(loadMoreTrigger)
  }

  setupPusherSubscriptions() {
    PusherManager.subscribeOnNewDialogMessage(this.universityIdValue, this.handleNewDialogMessage)
    PusherManager.subscribeOnDeletedDialogMessage(this.universityIdValue, this.handleDeletedDialogMessage)
  }

  handleNewDialogMessage(event) {
    if (event.dialog_id !== +this.dialogId)
      return

    if (event.reported_message_id) {
      this.handleReportedMessage(event.reported_message_id)
      return
    }

    const lastMessage = this.dialogContainerTarget.querySelector("[data-message-id]:last-child")
    if (lastMessage) {
      this.loadNextMessage(lastMessage)
      return
    }

    const url = this.messagesUrlBaseValue
    Turbo.visit(url, { frame: this.frameId })
  }

  handleReportedMessage(reportedMessageId) {
    const messageElement = this.dialogContainerTarget.querySelector(`[data-message-id="${reportedMessageId}"]`)

    if (messageElement) {
      this.refreshMessage(reportedMessageId)
    }

    this.refreshChatsList()
  }

  refreshChatsList() {
    if (!this.hasChatsListUrlValue)
      return

    turboFetch(this.chatsListUrlValue, { method: "GET" })
  }

  handleDeletedDialogMessage(event) {
    if (event.dialog_id !== +this.dialogId)
      return

    if (!event.message?.id)
      return

    this.removeMessageElement(event.message.id)
  }

  setupChatSubscriptions() {
    PusherManager.subscribeToChatReactions(this.dialogId, this.handleChatReaction)
    PusherManager.subscribeToChatPinMessage(this.dialogId, this.handleChatPinMessage)
    PusherManager.subscribeToChatUnpinMessage(this.dialogId, this.handleChatUnpinMessage)
    PusherManager.subscribeToChatDeleteMessage(this.dialogId, this.handleChatDeleteMessage)
    PusherManager.subscribeToChatUserBlocked(this.dialogId, this.handleChatUserBlocked)
    PusherManager.subscribeToChatUserUnblocked(this.dialogId, this.handleChatUserUnblocked)
  }

  handleChatReaction(event) {
    const messageId = event.message.id
    this.refreshMessage(messageId)
  }

  handleChatPinMessage(event) {
    if (event.chatId !== +this.dialogId)
      return

    this.refreshPinnedMessages()
    this.refreshMessage(event.message.id)
  }

  handleChatUnpinMessage(event) {
    if (event.chatId !== +this.dialogId)
      return

    this.refreshPinnedMessages()
    this.refreshMessage(event.message.id)
  }

  handleChatDeleteMessage(event) {
    if (event.chatId !== +this.dialogId)
      return

    this.removeMessageElement(event.message.id)

    this.refreshPinnedMessages()
  }

  handleChatUserBlocked(event) {
    if (this.isInitiatedByCurrentUser(event)) return

    this.refreshMessagesList()
  }

  handleChatUserUnblocked(event) {
    if (this.isInitiatedByCurrentUser(event)) return

    this.refreshMessagesList()
  }

  isInitiatedByCurrentUser(event) {
    return event.initiatorUserId === this.userIdValue
  }

  refreshMessagesList() {
    turboFetch(this.messagesUrlBaseValue, { method: 'GET' })
  }

  refreshPinnedMessages() {
    if (!this.hasPinnedMessagesUrlValue)
      return

    turboFetch(this.pinnedMessagesUrlValue, { method: 'GET' })
  }

  refreshMessage(messageId) {
    const baseUrl = this.messagesUrlBaseValue.replace('.turbo_stream', '')
    const url = `${baseUrl}/${messageId}`

    turboFetch(url, { method: 'GET' })
  }

  loadNextMessage(lastMessage) {
    const lastMessageId = lastMessage.dataset.messageId
    const url = `${this.nextMessageUrlBaseValue}?after_message_id=${lastMessageId}`

    const wasAtBottom = this.dialogContainerTarget.scrollTop + this.dialogContainerTarget.clientHeight >= this.dialogContainerTarget.scrollHeight
    Turbo.visit(url, { frame: this.frameId })

    if (wasAtBottom) {
      setTimeout(() => this.resetScroll(this.dialogContainerTarget), 150)
    }
  }

  onMessageClick(event) {
    const messageId = event.currentTarget.dataset.targetMessageId
    this.scrollToMessage(messageId)
  }

  scrollToMessage(messageId) {
    const message = this.dialogContainerTarget.querySelector(`[data-message-id="${messageId}"]`)

    if (message) {
      this.scrollToAndHighlightMessage(message)
    } else {
      this.loadMoreForReportedMessage(messageId)
    }
  }

  loadMore(targetMessageId = null) {
    const firstMessage = this.dialogContainerTarget.querySelector("[data-message-id]")
    const firstMessageId = firstMessage?.dataset.messageId

    if (!firstMessageId) return

    if (firstMessage?.dataset.messageType === "welcome_message") {
      this.hasMoreMessages = false
      return
    }

    if (firstMessageId === this.oldestMessageId) {
      this.hasMoreMessages = false
      return
    }

    this.isLoadingMore = true
    this.toggleSpinnerVisibility(false)

    let url = `${this.messagesUrlBaseValue}?before_message_id=${firstMessageId}`

    if (targetMessageId)
      url += `&target_message_id=${targetMessageId}`

    Turbo.visit(url, { frame: this.frameId })

    this.scroll(targetMessageId || firstMessageId)
  }

  scroll(id) {
    const container = this.dialogContainerTarget

    this.loadScrollObserver = new MutationObserver(() => {
      const scrollToElement = container.querySelector(`[data-message-id="${id}"]`)

      if (scrollToElement) {
        this.loadScrollObserver.disconnect()

        if (this.reportedMessageId === id) {
          this.scrollToAndHighlightMessage(scrollToElement)
          this.reportedMessageId = null
        } else {
          scrollToElement.scrollIntoView()
        }

        setTimeout(() => {
          this.isLoadingMore = false
          this.setAutoScroll(container)
          this.toggleSpinnerVisibility(true)
        }, 500)
      }
    })

    this.loadScrollObserver.observe(container, { childList: true, subtree: true })
  }

  toggleSpinnerVisibility(flag) {
    if (this.hasSpinnerTarget)
      this.spinnerTarget.classList.toggle("hidden", flag)
  }

  reply(event) {
    event.preventDefault()

    const text = event.currentTarget.dataset.text
    const name = event.currentTarget.dataset.name

    this.replyContainerTarget.classList.remove("hidden")
    this.replyNameTarget.textContent = name
    this.replyTextTarget.innerHTML = this.formatMentions(text)

    const parentMessageId = event.currentTarget.closest("[data-message-id]")?.dataset.messageId
    if (parentMessageId) {
      this.parentHiddenInputTarget.value = parentMessageId
    }
  }

  clearReply() {
    this.replyNameTarget.textContent = ""
    this.replyTextTarget.textContent = ""
    this.replyContainerTarget.classList.add("hidden")
    setTimeout(() => this.parentHiddenInputTarget.value = "", 600)
  }

  initReportedMessageScroll() {
    if (!this.loadReportedMessageValue) return

    const reportedMessageObserver = () => {
      const hasMessages = this.dialogContainerTarget.querySelector("[data-message-id]")
      const isVisible = !this.dialogContainerTarget.classList.contains("invisible")
      const isReady = hasMessages && isVisible && this.dialogId

      if (isReady) {
        this.scrollToReportedMessage()
      } else {
        requestAnimationFrame(reportedMessageObserver)
      }
    }

    requestAnimationFrame(reportedMessageObserver)
  }

  initTargetMessageScroll() {
    if (!this.hasTargetMessageIdValue || !this.targetMessageIdValue) return

    const waitForContainerReady = () => {
      const hasMessages = this.dialogContainerTarget.querySelector("[data-message-id]")
      const isVisible = !this.dialogContainerTarget.classList.contains("invisible")

      if (hasMessages && isVisible && this.dialogId) {
        const firstMessageId = this.dialogContainerTarget.querySelector("[data-message-id]")?.dataset.messageId
        this.hasMoreMessages = firstMessageId !== this.oldestMessageId
        this.scrollToMessage(this.targetMessageIdValue)
      } else {
        requestAnimationFrame(waitForContainerReady)
      }
    }

    requestAnimationFrame(waitForContainerReady)
  }

  async scrollToReportedMessage() {
    try {
      const reportedMessageId = await this.fetchReportedMessageId()

      if (reportedMessageId)
        this.scrollToMessage(reportedMessageId)
    } catch (error) {
      console.error("Error in scrollToReportedMessage:", error)
    }
  }

  async fetchReportedMessageId() {
    const url = this.reportedMessageUrlValue

    const response = await fetch(url)
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    }

    const data = await response.json()
    return data?.message_id || null
  }

  scrollToAndHighlightMessage(messageElement) {
    this.disableResetScrollTimeout()
    messageElement.scrollIntoView({ behavior: 'instant', block: 'center' })
    messageElement.classList.add('highlight')

    setTimeout(() => {
      messageElement.classList.remove('highlight')
    }, 3000)
  }

  removeMessageElement(messageId) {
    const messageElement = this.dialogContainerTarget.querySelector(`[data-message-id="${messageId}"]`)
    if (messageElement)
      messageElement.remove()
  }

  loadMoreForReportedMessage(targetMessageId) {
    this.reportedMessageId = targetMessageId
    this.loadMore(targetMessageId)
  }

  disableResetScrollTimeout() {
    this.isDisabledResetScroll = true
    setTimeout(() => this.isDisabledResetScroll = false, 800)
  }

  isChat() {
    return this.messagesUrlBaseValue?.includes('/chats/')
  }

  isActivePanel() {
    return this.element.dataset.active === "true"
  }

  get frameId() {
    return this.hasMessagesFrameIdValue
      ? this.messagesFrameIdValue
      : `dialog_messages_${this.dialogId}`
  }

  formatMentions(text) {
    const MENTION_PATTERN = /\{@\}\[(.+?)\]\((\d+)\)/g
    const escaped = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

    return escaped.replace(MENTION_PATTERN, (_, name, userId) => {
      const cssClass = parseInt(userId) === this.userIdValue ? 'chat-mention chat-mention-self' : 'chat-mention'
      return `<span class="${cssClass}">@${name}</span>`
    })
  }
}
